import {useEffect, useRef, useSyncExternalStore} from 'react';
import {useSelector} from 'react-redux';
import {
  selectPendingMeleeDamage,
  selectPhase,
  selectSelectedUnit,
  selectWarPhaseStage,
} from '../../../redux/reducers/sharedStateReducer/selectors';
import {selectInspectedHexKey, selectShowMeleeTargets} from '../../../redux/reducers/privateStateReducer/selectors';
import {checkIfMeleeResultHeld, subscribeToMeleeResultHolds} from '../../effects/meleeAnimationTracker';
import {checkIfMeleeDamageDetailsShown} from './checkIfMeleeDamageDetailsShown';

/** Придержан ли итог ближнего боя этого отряда для интерфейса: его удары (или удары по нему) еще идут */
export const useIsMeleeResultHeld = (unitId) => useSyncExternalStore(
  subscribeToMeleeResultHolds,
  () => Boolean(unitId) && checkIfMeleeResultHeld(unitId)
);

/**
 * Можно ли показывать в футере расчет урона выбранного отряда (панель
 * SelectedUnitMelee, ее заголовок и крестик снятия выбора).
 *
 * ПАНЕЛЬ ОТКРЫВАЕТСЯ ПОСЛЕ УДАРОВ. Расчет боя меняет состояние сразу, а на
 * поле в это время идет анимация ударов, и итог боя на плашках отрядов до ее
 * конца не показан (см. HexGridRenderer.showMeleeAnimation). Панель показывает
 * те же числа и открывается сама — отряд мог быть выбран еще до боя, — причем
 * поверх нижней части поля, где идут удары. Поэтому она ждет вместе с плашками.
 * Бой без анимации (скрытая вкладка, быстрая перемотка) панель открывает сразу.
 *
 * УЖЕ ОТКРЫТАЯ ПАНЕЛЬ НЕ ЗАКРЫВАЕТСЯ. Переброс костей повторяет удары отряда, а
 * кнопка переброса — в самой панели; футер мог быть открыт осмотром ячейки, с
 * которого он сам переключается на отряд-участника (см. Footer). Прятать
 * панель в этих случаях незачем — поле она уже закрывала, а мигание только
 * мешало бы. То же правило — у панели результата стрельбы (см.
 * useShootingResultRevealed).
 *
 * После выбора приоритетной цели панель возвращается, когда отряд отбил
 * удары заново: на время выбора она и так была скрыта.
 *
 * Хук один на футер (см. Footer): его ответ раздается заголовку, тулбару,
 * крестику и футеру зрителя, чтобы все судили одинаково.
 *
 * @returns {boolean}
 */
export const useMeleeResultRevealed = () => {
  const phase = useSelector(selectPhase);
  const warPhaseStage = useSelector(selectWarPhaseStage);
  const selectedUnit = useSelector(selectSelectedUnit);
  const showMeleeTargets = useSelector(selectShowMeleeTargets);
  const pendingMeleeDamage = useSelector(selectPendingMeleeDamage);
  const inspectedHexKey = useSelector(selectInspectedHexKey);

  const hasDetails = checkIfMeleeDamageDetailsShown({
    phase, warPhaseStage, selectedUnit, showMeleeTargets, pendingMeleeDamage,
  });
  const isResultHeld = useIsMeleeResultHeld(selectedUnit?.id);

  // Была ли панель футера открыта на прошлой отрисовке: расчетом урона или
  // осмотром ячейки (он показывается поверх фазового содержимого)
  const wasPanelOpenRef = useRef(false);
  const isRevealed = hasDetails && (!isResultHeld || wasPanelOpenRef.current);
  const isPanelOpen = isRevealed || Boolean(inspectedHexKey);
  useEffect(() => {
    wasPanelOpenRef.current = isPanelOpen;
  });

  return isRevealed;
};
